import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { trackEvent } from '../../services/analytics';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App error:', error, errorInfo);
    // Error name only -- messages can carry user input or internal detail.
    trackEvent('app_error', { error_type: error.name || 'Error' });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            bgcolor: '#1A1A1D',
            color: '#B9BBBE',
          }}
        >
          <Typography variant="h5" sx={{ color: '#F04747', mb: 2 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, maxWidth: 480, textAlign: 'center' }}>
            {this.state.error.message}
          </Typography>
          <Button variant="contained" onClick={this.handleRetry} sx={{ mt: 2 }}>
            Try again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
